import { MOCK_USER } from "@/constants/mockData";
import { ThemeColors, useColors } from "@/constants/theme";
import { useAppStore } from "@/stores/useStore";
import { Card as LensCard, Post, Profile, ReactionType, Visibility } from "@/types";
import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import { useRouter } from "expo-router";
import {
  ChevronRight,
  EyeOff,
  Flag,
  Heart,
  Lightbulb,
  Link2,
  MessageCircle,
  Sparkles,
  Trash2,
  Zap,
} from "lucide-react-native";
import React, { useMemo, useState } from "react";
import { Alert, Image, Pressable, StyleSheet, View } from "react-native";
import { Avatar, Card, Text, TextInput } from "react-native-paper";

interface PostCardProps {
  post: Post;
}

const VISIBILITY_LABELS: Record<Visibility, string> = {
  private: "自分のみ",
  class: "クラス",
  school: "学校",
  public: "公開",
};

const REACTION_BUTTONS: {
  type: ReactionType;
  label: string;
  color: string;
  icon: React.ComponentType<{ size: number; color: string; fill?: string }>;
}[] = [
  { type: "empathy", label: "共感", color: "#e53e3e", icon: Heart },
  { type: "surprise", label: "驚き", color: "#d69e2e", icon: Zap },
  { type: "deep", label: "深い", color: "#3182ce", icon: MessageCircle },
  { type: "useful", label: "使いたい", color: "#38a169", icon: Lightbulb },
  { type: "connected", label: "つながった", color: "#805ad5", icon: Link2 },
];

const COMMENT_PRESETS = [
  "なるほど",
  "使ってみたい",
  "別のカードともつながりそう",
];

const createFallbackProfile = (userId: string): Profile => ({
  id: userId,
  display_name: "学習者",
  role: "student",
});

export default function PostCard({ post }: PostCardProps) {
  const {
    currentUser,
    cards,
    reactions,
    addReaction,
    getPostReactionCounts,
    addComment,
    getCommentsForPost,
    getProfileById,
    reportPost,
    hidePost,
    deletePost,
    subjects,
  } = useAppStore();
  const router = useRouter();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const [showComments, setShowComments] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [failedMediaUris, setFailedMediaUris] = useState<string[]>([]);

  const user =
    getProfileById(post.user_id) ??
    (post.user_id === MOCK_USER.id ? MOCK_USER : createFallbackProfile(post.user_id));
  const lenses = post.card_ids
    .map((id) => cards.find((c) => c.id === id))
    .filter(Boolean) as LensCard[];
  const reactionCounts = getPostReactionCounts(post.id);
  const comments = getCommentsForPost(post.id);
  const isOwnPost = post.user_id === currentUser?.id;
  const canManage =
    isOwnPost ||
    currentUser?.role === "teacher" ||
    currentUser?.role === "admin";

  const requireLogin = () => {
    if (currentUser) return true;
    Alert.alert("ログインが必要です", "プロフィールからログインしてください。");
    return false;
  };

  const handleReaction = (type: ReactionType) => {
    if (!requireLogin()) return;
    addReaction(post.id, type).catch((error) => {
      console.warn("Failed to update reaction", error);
      Alert.alert("リアクションできませんでした", "もう一度お試しください。");
    });
  };

  const handleAddComment = (body: string, preset = false) => {
    if (!requireLogin()) return;
    addComment(post.id, body, preset)
      .then(() => {
        setCommentBody("");
        setShowComments(true);
      })
      .catch((error) => {
        console.warn("Failed to add comment", error);
        Alert.alert("コメントできませんでした", "もう一度お試しください。");
      });
  };

  const handleReport = () => {
    if (!requireLogin()) return;
    reportPost(post.id, "不適切または個人情報の可能性")
      .then(() => {
        Alert.alert("通報しました", "この投稿はあなたのタイムラインから非表示になります。");
      })
      .catch((error) => {
        console.warn("Failed to report post", error);
        Alert.alert("通報できませんでした", "もう一度お試しください。");
      });
  };

  const handleDelete = () => {
    Alert.alert("投稿を削除しますか？", "削除した投稿は一覧から表示されません。", [
      { text: "キャンセル", style: "cancel" },
      {
        text: "削除",
        style: "destructive",
        onPress: () => {
          deletePost(post.id).catch((error) => {
            console.warn("Failed to delete post", error);
            Alert.alert("削除できませんでした", "もう一度お試しください。");
          });
        },
      },
    ]);
  };

  return (
    <Card style={styles.card} mode="elevated">
      <Card.Title
        title={user.display_name}
        subtitle={`${formatDistanceToNow(new Date(post.created_at), {
          addSuffix: true,
          locale: ja,
        })} • ${VISIBILITY_LABELS[post.visibility]}`}
        left={(props) =>
          user.avatar_url ? (
            <Avatar.Image {...props} source={{ uri: user.avatar_url }} />
          ) : (
            <Avatar.Text {...props} label={user.display_name.slice(0, 2)} />
          )
        }
        right={() => (
          <View style={styles.titleActions}>
            {!isOwnPost && (
              <>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => hidePost(post.id)}
                  style={styles.iconAction}
                >
                  <EyeOff size={18} color={c.textMuted} />
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  onPress={handleReport}
                  style={styles.iconAction}
                >
                  <Flag size={18} color={c.textMuted} />
                </Pressable>
              </>
            )}
            {canManage && (
              <Pressable
                accessibilityRole="button"
                onPress={handleDelete}
                style={styles.iconAction}
              >
                <Trash2 size={18} color={c.danger} />
              </Pressable>
            )}
          </View>
        )}
      />
      <Card.Content>
        <View style={styles.discoveryBadge}>
          <Sparkles size={14} color={c.primary} />
          <Text style={styles.discoveryText}>{post.discovery_type}</Text>
        </View>

        <Text variant="bodyLarge" style={styles.body}>
          {post.body}
        </Text>

        {post.media_urls && post.media_urls.length > 0 && (
          <View style={styles.mediaGrid}>
            {post.media_urls.map((uri) => {
              const hasFailed = failedMediaUris.includes(uri);

              return hasFailed ? (
                <View key={uri} style={[styles.mediaImage, styles.mediaError]}>
                  <Text style={styles.mediaErrorText}>
                    画像を読み込めませんでした
                  </Text>
                </View>
              ) : (
                <Image
                  key={uri}
                  source={{ uri }}
                  style={styles.mediaImage}
                  onError={() => {
                    setFailedMediaUris((currentUris) =>
                      currentUris.includes(uri)
                        ? currentUris
                        : [...currentUris, uri],
                    );
                  }}
                />
              );
            })}
          </View>
        )}

        {post.reflection && (
          <View style={styles.reflectionBox}>
            <Text style={styles.reflectionLabel}>振り返り</Text>
            <Text style={styles.reflectionText}>{post.reflection}</Text>
          </View>
        )}

        {lenses.length > 0 && (
          <View style={styles.lensesContainer}>
            <Text style={styles.lensesLabel}>使ったレンズ</Text>
            {lenses.map((lens) => {
              const subject = subjects.find((s) => s.id === lens.subject_id);
              return (
                <Pressable
                  key={lens.id}
                  accessibilityRole="button"
                  onPress={() =>
                    router.push({
                      pathname: "/cards/[cardId]",
                      params: { cardId: lens.id },
                    })
                  }
                  style={({ pressed }) => [
                    styles.lensChip,
                    {
                      borderColor: subject?.color_code || "#ccc",
                      borderLeftColor: subject?.color_code || "#ccc",
                    },
                    pressed && styles.lensChipPressed,
                  ]}
                >
                  <View style={styles.lensInfo}>
                    <Text
                      style={[
                        styles.lensTitle,
                        { color: subject?.color_code || "#666" },
                      ]}
                    >
                      {lens.title}
                    </Text>
                    <Text style={styles.lensName}>{lens.lens_name}</Text>
                  </View>
                  <Text style={styles.subjectName}>{subject?.name}</Text>
                  <ChevronRight size={16} color={c.textMuted} />
                </Pressable>
              );
            })}
          </View>
        )}
      </Card.Content>

      <View style={styles.reactionContainer}>
        {REACTION_BUTTONS.map(({ type, label, color, icon: Icon }) => {
          const isActive = reactions.some(
            (reaction) =>
              reaction.post_id === post.id &&
              reaction.user_id === currentUser?.id &&
              reaction.reaction_type === type,
          );

          return (
            <Pressable
              key={type}
              style={[
                styles.reactionButton,
                isActive && { backgroundColor: `${color}16` },
              ]}
              onPress={() => handleReaction(type)}
            >
              <Icon
                size={16}
                color={isActive ? color : c.textMuted}
                fill={type === "empathy" && isActive ? color : "none"}
              />
              <Text
                style={[
                  styles.reactionText,
                  isActive && { color, fontWeight: "700" },
                ]}
              >
                {label}
                {reactionCounts[type] > 0 ? ` ${reactionCounts[type]}` : ""}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.commentSection}>
        <Pressable
          accessibilityRole="button"
          onPress={() => setShowComments((value) => !value)}
          style={styles.commentToggle}
        >
          <MessageCircle size={16} color={c.textSecondary} />
          <Text style={styles.commentToggleText}>
            コメント {comments.length > 0 ? comments.length : ""}
          </Text>
        </Pressable>

        {showComments && (
          <View style={styles.commentBody}>
            <View style={styles.presetRow}>
              {COMMENT_PRESETS.map((preset) => (
                <Pressable
                  key={preset}
                  accessibilityRole="button"
                  style={styles.presetChip}
                  onPress={() => handleAddComment(preset, true)}
                >
                  <Text style={styles.presetText}>{preset}</Text>
                </Pressable>
              ))}
            </View>

            {comments.map((comment) => (
              <View key={comment.id} style={styles.commentItem}>
                <Text style={styles.commentAuthor}>
                  {getProfileById(comment.user_id)?.display_name ?? "学習者"}
                </Text>
                <Text style={styles.commentText}>{comment.body}</Text>
              </View>
            ))}

            <View style={styles.commentInputRow}>
              <TextInput
                dense
                mode="outlined"
                value={commentBody}
                onChangeText={setCommentBody}
                placeholder="短いコメント"
                style={styles.commentInput}
              />
              <Pressable
                accessibilityRole="button"
                onPress={() => handleAddComment(commentBody)}
                style={styles.commentSend}
              >
                <Text style={styles.commentSendText}>送信</Text>
              </Pressable>
            </View>
          </View>
        )}
      </View>
    </Card>
  );
}

const createStyles = (c: ThemeColors) => StyleSheet.create({
  card: {
    marginHorizontal: 16,
    marginVertical: 8,
    backgroundColor: c.surface,
  },
  titleActions: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 8,
    gap: 4,
  },
  iconAction: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
  },
  discoveryBadge: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: c.primarySoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 14,
    marginBottom: 10,
  },
  discoveryText: {
    color: c.primaryDark,
    fontSize: 12,
    fontWeight: "700",
  },
  body: {
    marginBottom: 12,
    lineHeight: 24,
    color: c.textPrimary,
  },
  mediaGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  mediaImage: {
    width: "48%",
    aspectRatio: 1.2,
    borderRadius: 8,
    backgroundColor: c.surfaceAlt,
  },
  mediaError: {
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: c.border,
  },
  mediaErrorText: {
    color: c.textSecondary,
    fontSize: 12,
    fontWeight: "700",
    textAlign: "center",
    paddingHorizontal: 8,
  },
  reflectionBox: {
    backgroundColor: c.primarySoft,
    borderLeftWidth: 4,
    borderLeftColor: c.primary,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    marginBottom: 12,
  },
  reflectionLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: c.primaryDark,
    marginBottom: 4,
  },
  reflectionText: {
    fontSize: 13,
    color: c.textPrimary,
    lineHeight: 18,
  },
  lensesContainer: {
    marginTop: 12,
    gap: 8,
  },
  lensesLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: c.textSecondary,
    marginBottom: 4,
  },
  lensChip: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderLeftWidth: 4,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
    backgroundColor: c.surfaceAlt,
  },
  lensChipPressed: {
    opacity: 0.65,
  },
  lensInfo: {
    flex: 1,
  },
  lensTitle: {
    fontSize: 13,
    fontWeight: "700",
    marginBottom: 2,
  },
  lensName: {
    fontSize: 11,
    color: c.textSecondary,
    fontStyle: "italic",
  },
  subjectName: {
    color: c.textMuted,
    fontSize: 11,
    fontWeight: "700",
  },
  reactionContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 6,
    borderTopWidth: 1,
    borderTopColor: c.border,
    gap: 8,
  },
  reactionButton: {
    minWidth: "30%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: c.surfaceAlt,
  },
  reactionText: {
    fontSize: 11,
    color: c.textSecondary,
    fontWeight: "600",
  },
  commentSection: {
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  commentToggle: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    paddingVertical: 8,
  },
  commentToggleText: {
    color: c.textSecondary,
    fontWeight: "700",
    fontSize: 12,
  },
  commentBody: {
    gap: 8,
  },
  presetRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
  },
  presetChip: {
    backgroundColor: c.surfaceAlt,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  presetText: {
    color: c.textSecondary,
    fontSize: 12,
    fontWeight: "700",
  },
  commentItem: {
    backgroundColor: c.surfaceAlt,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  commentAuthor: {
    color: c.textSecondary,
    fontSize: 11,
    fontWeight: "700",
    marginBottom: 2,
  },
  commentText: {
    color: c.textPrimary,
    fontSize: 13,
    lineHeight: 18,
  },
  commentInputRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  commentInput: {
    flex: 1,
    backgroundColor: c.surface,
  },
  commentSend: {
    height: 40,
    minWidth: 58,
    borderRadius: 8,
    backgroundColor: c.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  commentSendText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 12,
  },
});
